import { memo, ReactElement, useContext, useMemo } from 'react';
import * as R from 'ramda';

import { numberToLocaleString } from '@atom/utilities/currencyUtility';
import textUtils from '@atom/utilities/textDisplayUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import ExpendituresChartContext from './ExpendituresChartContext';
import {
  BarIcon,
  getFixedBarColor,
  getMonthOnly,
  getRemainingPercentDisplay,
  GroupKeys,
  LineIcon,
  RechartPayload,
} from './expendituresChartUtils';

import classes from './chartTooltip.module.css';

const TITLE_MAX_CHARS = 23;

interface Props {
  active?: boolean;
  payload?: RechartPayload[];
  label?: string;
}

const ExpendituresChartTooltip = ({ active, payload, label = '' }: Props) => {
  const { actualFields, futureFixedRemainingFields, data } = useContext(
    ExpendituresChartContext,
  );

  // Display only month when comparing multiple budgets
  const tooltipHeading = useMemo(() => {
    const remainingLines = R.uniq(
      R.map(
        R.path(['stroke']),
        payload.filter(({ strokeWidth }) => !R.isNil(strokeWidth)),
      ),
    );
    return remainingLines.length > 1 ? getMonthOnly(label) : label;
  }, [label]);

  if (!active || isNilOrEmpty(payload)) {
    return null;
  }

  const fieldsMap = R.groupBy(R.prop('dataKey'), payload);
  const getField = (field, val) => R.pathOr('', [field, 0, val], fieldsMap);

  // Hide Columns with no data
  const hideActualCostCol = R.isEmpty(
    actualFields.filter(
      fieldKey =>
        !R.isNil(R.pathOr(null, [fieldKey, 0, 'value'], fieldsMap)) &&
        R.pathOr(0, [fieldKey, 0, 'value'], fieldsMap) !== 0,
    ),
  );

  const hideFutureFixedCol = R.isEmpty(
    R.intersection(R.keys(fieldsMap), futureFixedRemainingFields),
  );

  const tableRow = (row: string, idx: number): ReactElement<any> => {
    const budgetName = row.split(GroupKeys.FUTURE_FIXED_REMAINING)[0];
    const getKey = (group: GroupKeys) => `${budgetName}${group}`;
    const getValue = (group: GroupKeys) =>
      Number(getField(getKey(group), 'value'));

    const remainingValue: number =
      getValue(GroupKeys.REMAINING) ||
      getValue(GroupKeys.FUTURE_FIXED_REMAINING);

    // Get total value for percent from initial total remaining in first col
    const totalValue: number = data[0][getKey(GroupKeys.REMAINING)];

    return (
      <tr key={row} className={classes.ttTableRow}>
        <th className={classes.ttTableCell} scope="row">
          {textUtils.truncate(budgetName.trim(), TITLE_MAX_CHARS)}
        </th>
        <td className={classes.ttTableCell}>
          {remainingValue !== 0 && (
            <div className={classes.ttCellAlignment}>
              {LineIcon(
                getField(
                  getKey(
                    getValue(GroupKeys.REMAINING) !== 0
                      ? GroupKeys.REMAINING
                      : GroupKeys.FUTURE_FIXED_REMAINING,
                  ),
                  'stroke',
                ),
              )}
              <span>
                {getRemainingPercentDisplay(remainingValue, totalValue)}
              </span>
            </div>
          )}
        </td>
        {!hideActualCostCol && (
          <td className={classes.ttTableCell}>
            {getValue(GroupKeys.ACTUAL) !== 0 && (
              <div className={classes.ttCellAlignment}>
                {BarIcon(getField(getKey(GroupKeys.ACTUAL), 'fill'))}
                <span>{numberToLocaleString(getValue(GroupKeys.ACTUAL))}</span>
              </div>
            )}
          </td>
        )}
        {!hideFutureFixedCol && (
          <td className={classes.ttTableCell}>
            {getValue(GroupKeys.FUTURE_FIXED) !== 0 && (
              <div className={classes.ttCellAlignment}>
                {BarIcon(getFixedBarColor(idx))}
                <span>
                  {numberToLocaleString(getValue(GroupKeys.FUTURE_FIXED))}
                </span>
              </div>
            )}
          </td>
        )}
      </tr>
    );
  };

  return (
    <div className={classes.ttContainer}>
      <div className={classes.ttTitle}>{tooltipHeading}</div>
      <table className={classes.ttTable}>
        <thead>
          <tr className={classes.ttTableRow}>
            <th className={classes.ttTableHeading} scope="col" />
            <th className={classes.ttTableHeading} scope="col">
              Remaining
            </th>
            {!hideActualCostCol && (
              <th className={classes.ttTableHeading} scope="col">
                Actual Cost
              </th>
            )}
            {!hideFutureFixedCol && (
              <th className={classes.ttTableHeading} scope="col">
                Future Fixed Cost
              </th>
            )}
          </tr>
        </thead>
        <tbody>{futureFixedRemainingFields.map(tableRow)}</tbody>
      </table>
    </div>
  );
};

export default memo(ExpendituresChartTooltip);
